/**
 * Optional language-intelligence adapter for the editor kernel.
 *
 * Completions, hover, and go-to-definition are Compartment-enabled only when
 * a host supplies an adapter. The kernel never talks IPC; it asks this
 * adapter with the live path, document, and UTF-16 offset, then maps a
 * bounded, text-only view. Hover DOM is `textContent` only — no HTML.
 * Failures are empty results, never thrown into the editor.
 */
import {
  autocompletion,
  type Completion,
  type CompletionContext,
  type CompletionResult,
} from "@codemirror/autocomplete";
import { keymap, hoverTooltip, EditorView, type Tooltip } from "@codemirror/view";
import type { Extension } from "@codemirror/state";

export const INTEL_MAX_COMPLETIONS = 80;
export const INTEL_MAX_LABEL = 120;
export const INTEL_MAX_DETAIL = 200;
export const INTEL_MAX_KIND = 40;
export const INTEL_MAX_HOVER = 4_000;
export const INTEL_MAX_DEFINITIONS = 16;

export interface LanguageCompletion {
  readonly label: string;
  readonly kind?: string;
  readonly detail?: string;
}

export interface LanguageHover {
  readonly display: string;
  readonly documentation?: string;
}

export interface LanguageDefinition {
  readonly path: string;
  readonly line: number;
  readonly column: number;
}

export interface LanguageQuery {
  readonly path: string;
  readonly document: string;
  readonly offset: number;
  readonly signal?: AbortSignal;
}

export interface LanguageIntelligence {
  complete?(query: LanguageQuery): Promise<readonly LanguageCompletion[] | null>;
  hover?(query: LanguageQuery): Promise<LanguageHover | null>;
  definition?(query: LanguageQuery): Promise<readonly LanguageDefinition[] | null>;
}

export interface IntelligenceHooks {
  onDefinition?: (definitions: readonly LanguageDefinition[]) => void;
  onDefinitionError?: (message: string) => void;
}

const COMPLETION_TYPES = new Set([
  "class",
  "constant",
  "enum",
  "function",
  "interface",
  "keyword",
  "method",
  "namespace",
  "property",
  "text",
  "type",
  "variable",
]);

const KIND_TO_TYPE: Record<string, string> = {
  class: "class",
  interface: "interface",
  enum: "enum",
  "enum member": "enum",
  function: "function",
  method: "method",
  property: "property",
  keyword: "keyword",
  module: "namespace",
  script: "namespace",
  directory: "namespace",
  alias: "type",
  type: "type",
  const: "constant",
  let: "variable",
  var: "variable",
  variable: "variable",
  parameter: "variable",
  string: "text",
  text: "text",
  constant: "constant",
  namespace: "namespace",
};

/**
 * Drop C0 controls except tab/LF/CR. Walks code points so a control character
 * never sits in a regex literal (`no-control-regex`).
 */
export function stripIntelControls(value: string): string {
  let out = "";
  for (const ch of value) {
    const code = ch.codePointAt(0);
    if (code === undefined) continue;
    if (code <= 0x08 || code === 0x0b || code === 0x0c || (code >= 0x0e && code <= 0x1f)) continue;
    out += ch;
  }
  return out;
}

function clampText(value: unknown, max: number): string {
  if (typeof value !== "string" || max <= 0) return "";
  const stripped = stripIntelControls(value);
  if (stripped.length <= max) return stripped;
  if (max === 1) return "…";
  return `${stripped.slice(0, max - 1)}…`;
}

function safeKind(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const raw = value.trim().toLowerCase().slice(0, INTEL_MAX_KIND);
  const mapped = KIND_TO_TYPE[raw];
  if (mapped && COMPLETION_TYPES.has(mapped)) return mapped;
  if (/^[a-z][a-z0-9_-]*$/.test(raw) && COMPLETION_TYPES.has(raw)) return raw;
  return undefined;
}

export function mapCompletions(items: readonly LanguageCompletion[]): Completion[] {
  const out: Completion[] = [];
  for (const item of items) {
    if (out.length >= INTEL_MAX_COMPLETIONS) break;
    const label = clampText(item.label, INTEL_MAX_LABEL);
    if (!label) continue;
    const completion: Completion = { label };
    const detail = clampText(item.detail, INTEL_MAX_DETAIL);
    if (detail) completion.detail = detail;
    const type = safeKind(item.kind);
    if (type) completion.type = type;
    out.push(completion);
  }
  return out;
}

export function renderLanguageHover(info: LanguageHover): HTMLElement {
  const root = document.createElement("div");
  root.className = "cm-tavsin-hover";
  const display = document.createElement("div");
  display.className = "cm-tavsin-hover-display";
  display.textContent = clampText(info.display, INTEL_MAX_HOVER);
  root.appendChild(display);
  const docs = clampText(info.documentation, INTEL_MAX_HOVER);
  if (docs) {
    const documentation = document.createElement("div");
    documentation.className = "cm-tavsin-hover-docs";
    documentation.textContent = docs;
    root.appendChild(documentation);
  }
  return root;
}

function queryOf(
  path: string,
  document: string,
  offset: number,
  signal?: AbortSignal,
): LanguageQuery {
  const max = document.length;
  const pos = Number.isFinite(offset) ? Math.min(Math.max(0, Math.floor(offset)), max) : 0;
  return signal ? { path, document, offset: pos, signal } : { path, document, offset: pos };
}

function abortSignal(context: CompletionContext): AbortSignal {
  const ac = new AbortController();
  context.addEventListener("abort", () => ac.abort(), { onDocChange: true });
  return ac.signal;
}

async function completeSource(
  adapter: LanguageIntelligence,
  pathOf: () => string,
  generationOf: () => number,
  context: CompletionContext,
): Promise<CompletionResult | null> {
  const complete = adapter.complete;
  if (!complete) return null;
  const word = context.matchBefore(/[\w$]*/) ?? { from: context.pos, to: context.pos, text: "" };
  if (word.from === word.to && !context.explicit) return null;
  const generation = generationOf();
  const path = pathOf();
  try {
    const items = await complete(queryOf(path, context.state.doc.toString(), context.pos, abortSignal(context)));
    if (context.aborted || generation !== generationOf() || pathOf() !== path) return null;
    if (!items || items.length === 0) return null;
    return { from: word.from, options: mapCompletions(items), validFor: /^[\w$]*$/ };
  } catch {
    return null;
  }
}

async function hoverSource(
  adapter: LanguageIntelligence,
  pathOf: () => string,
  generationOf: () => number,
  view: EditorView,
  pos: number,
): Promise<Tooltip | null> {
  const hover = adapter.hover;
  if (!hover) return null;
  const generation = generationOf();
  const path = pathOf();
  try {
    const info = await hover(queryOf(path, view.state.doc.toString(), pos));
    if (generation !== generationOf() || pathOf() !== path || !info) return null;
    const display = clampText(info.display, INTEL_MAX_HOVER);
    if (!display) return null;
    const word = view.state.wordAt(pos);
    return {
      pos: word?.from ?? pos,
      end: word?.to ?? pos,
      create: () => ({
        dom: renderLanguageHover({
          display,
          ...(info.documentation !== undefined ? { documentation: info.documentation } : {}),
        }),
      }),
    };
  } catch {
    return null;
  }
}

async function requestDefinition(
  adapter: LanguageIntelligence,
  pathOf: () => string,
  generationOf: () => number,
  hooks: IntelligenceHooks,
  view: EditorView,
  offset: number,
): Promise<void> {
  const definition = adapter.definition;
  if (!definition) return;
  const generation = generationOf();
  const path = pathOf();
  try {
    const defs = await definition(queryOf(path, view.state.doc.toString(), offset));
    if (generation !== generationOf() || pathOf() !== path) return;
    const bounded = (defs ?? []).slice(0, INTEL_MAX_DEFINITIONS).filter((d) => {
      return (
        typeof d.path === "string" &&
        d.path.length > 0 &&
        Number.isInteger(d.line) &&
        d.line >= 1 &&
        Number.isInteger(d.column) &&
        d.column >= 1
      );
    });
    hooks.onDefinition?.(bounded);
  } catch (error) {
    if (generation !== generationOf() || pathOf() !== path) return;
    const message = error instanceof Error && error.message.trim() ? error.message : "Definition lookup failed.";
    hooks.onDefinitionError?.(message);
  }
}

const HOVER_THEME = EditorView.theme({
  ".cm-tavsin-hover": {
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontSize: "12px",
    padding: "6px 8px",
    maxWidth: "480px",
    whiteSpace: "pre-wrap",
  },
  ".cm-tavsin-hover-docs": {
    marginTop: "6px",
    opacity: "0.8",
  },
});

/**
 * Completions + hover + F12 / Mod-click definition. Empty when no adapter.
 */
export function intelligenceExtension(
  adapter: LanguageIntelligence | undefined,
  pathOf: () => string,
  generationOf: () => number,
  hooks: IntelligenceHooks,
): Extension {
  if (!adapter) return [];
  const goTo = (view: EditorView, offset: number): boolean => {
    void requestDefinition(adapter, pathOf, generationOf, hooks, view, offset);
    return true;
  };
  return [
    autocompletion({
      override: [(context) => completeSource(adapter, pathOf, generationOf, context)],
    }),
    hoverTooltip((view, pos) => hoverSource(adapter, pathOf, generationOf, view, pos)),
    keymap.of([
      {
        key: "F12",
        preventDefault: true,
        run: (view) => goTo(view, view.state.selection.main.head),
      },
    ]),
    EditorView.domEventHandlers({
      click(event, view) {
        if (event.button !== 0 || !(event.metaKey || event.ctrlKey)) return false;
        const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
        if (pos === null) return false;
        event.preventDefault();
        return goTo(view, pos);
      },
    }),
    HOVER_THEME,
  ];
}
